package com.yendo.mx.utils

import com.yendo.mx.models.ConfiguracionZona
import com.yendo.mx.models.ZonaDirectorio
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow

/**
 * Singleton que gestiona el estado global de la zona y configuración activa.
 */
object ConfigManager {

    // Estado para la UI: ¿Estamos cargando, hay éxito o no hay cobertura?
    private val _estadoConfig = MutableStateFlow<EstadoCarga>(EstadoCarga.Vacio)
    val estadoConfig: StateFlow<EstadoCarga> = _estadoConfig

    // Datos que el resto de la app (Carrito, Checkout) consultará
    var configActiva: ConfiguracionZona? = null
    var zonaData: ZonaDirectorio? = null

    // 🚀 NUEVO: Almacena las coordenadas GPS reales para alertas de lejanía (Estado Reactivo)
    val latGPSReal = MutableStateFlow(0.0)
    val lngGPSReal = MutableStateFlow(0.0)

    // 🚀 NUEVO: Caché de los últimos parámetros para permitir recarga manual (Pull to Refresh)
    private var lastLat: Double = 0.0
    private var lastLng: Double = 0.0
    private var lastCP: String = ""

    /**
     * Procesa la ubicación del usuario para activar una zona.
     * @param lat Latitud del pin en el mapa
     * @param lng Longitud del pin en el mapa
     * @param cp Código Postal detectado (para búsqueda rápida)
     */
    suspend fun procesarUbicacion(lat: Double, lng: Double, cp: String) {
        lastLat = lat
        lastLng = lng
        lastCP = cp
        
        _estadoConfig.value = EstadoCarga.Cargando
        val db = Firebase.firestore

        try {
            // 1. Buscamos en el "Portero" (Zonas_Directorio) por Código Postal
            // 🚀 AÑADIDO: Bloqueo de seguridad para evitar cuelgues anónimos
            val query = kotlinx.coroutines.withTimeout(5000L) {
                db.collection("Zonas_Directorio")
                    .where { "codigos_postales" contains cp }
                    .get()
            }

            // 🚀 ESCUDO 1: Protegemos la lectura del directorio
            val document = query.documents.firstOrNull()
            var zonaEncontrada = try {
                document?.data<ZonaDirectorio>()?.let { 
                    if (it.id_zona.isBlank()) it.copy(id_zona = document.id) else it
                }
            } catch (e: Exception) {
                println("🚨 [CONFIG MANAGER] Error al mapear ZonaDirectorio: ${e.message}")
                e.printStackTrace()
                null
            }

            // 🚀 FALLBACK INTELIGENTE DE AUTO-APRENDIZAJE: Si el C.P. no está en la base pero cae dentro del polígono
            if (zonaEncontrada == null || zonaEncontrada.status != "activo") {
                val zonasActivas = try {
                    kotlinx.coroutines.withTimeout(5000L) {
                        db.collection("Zonas_Directorio")
                            .where { "status" equalTo "activo" }
                            .get()
                    }
                } catch (e: Exception) { null }

                val zonaPorPoligono = zonasActivas?.documents?.mapNotNull { d ->
                    try { d.data<ZonaDirectorio>().let { if (it.id_zona.isBlank()) it.copy(id_zona = d.id) else it } } catch (e: Exception) { null }
                }?.firstOrNull { z ->
                    GeoUtils.validarCoberturaTotal(lat, lng, z)
                }

                if (zonaPorPoligono != null) {
                    if (cp.isNotBlank()) {
                        try {
                            db.collection("Zonas_Directorio").document(zonaPorPoligono.id_zona).update(
                                "codigos_postales" to dev.gitlive.firebase.firestore.FieldValue.arrayUnion(cp)
                            )
                            println("✨ [AUTO-APRENDIZAJE] Código Postal $cp asociado automáticamente a la zona ${zonaPorPoligono.id_zona}")
                        } catch (e: Exception) {}
                    }
                    zonaEncontrada = zonaPorPoligono
                }
            }

            if (zonaEncontrada != null && zonaEncontrada.status == "activo") {

                // 2. Usamos el Motor Matemático (GeoUtils) para validar polígonos (Centro/Cobach)
                val tieneCobertura = GeoUtils.validarCoberturaTotal(lat, lng, zonaEncontrada)

                if (tieneCobertura) {
                    // 3. Descargamos el "Manual" (ConfiguracionZona) usando el ID de la zona
                    val configDoc = kotlinx.coroutines.withTimeout(5000L) {
                        db.collection("Configuraciones")
                            .document(zonaEncontrada.id_zona)
                            .get()
                    }

                    // 🚀 ESCUDO 2: EL DETECTOR DE CHOQUES PRINCIPAL
                    val configRaw = try {
                        configDoc.data<ConfiguracionZona>()
                    } catch (e: Exception) {
                        println("🚨 [CONFIG MANAGER] ¡ERROR FATAL DE MAPEADO!")
                        println("🚨 [CONFIG MANAGER] Kotlin no pudo procesar el JSON de ConfiguracionZona.")
                        println("🚨 [CONFIG MANAGER] Causa exacta: ${e.message}")
                        e.printStackTrace()
                        null
                    }

                    if (configRaw != null) {
                        // 🚀 ASEGURAMOS QUE EL ID_ZONA ESTÉ POBLADO (Inyectamos el del documento si viene vacío)
                        val config = if (configRaw.id_zona.isBlank()) {
                            configRaw.copy(id_zona = zonaEncontrada.id_zona)
                        } else {
                            configRaw
                        }

                        println("✅ [CONFIG MANAGER] Configuración descargada y mapeada correctamente para: ${config.id_zona}")
                        configActiva = config
                        zonaData = zonaEncontrada
                        _estadoConfig.value = EstadoCarga.Exito(
                            config = config,
                            id_zona = config.id_zona,
                            nombreZona = zonaEncontrada.nombre_mostrable
                        )
                    } else {
                        println("❌ [CONFIG MANAGER] El documento de configuración falló o es nulo.")
                        _estadoConfig.value = EstadoCarga.Error("Error de lectura en la configuración de la zona")
                    }
                } else {
                    // Está en el CP correcto pero fuera de tus polígonos (zona muerta)
                    limpiarDatos()
                    _estadoConfig.value = EstadoCarga.SinCobertura
                }
            } else {
                // No operamos en ese Código Postal
                limpiarDatos()
                _estadoConfig.value = EstadoCarga.SinCobertura
            }

        } catch (e: Exception) {
            println("🚨 [CONFIG MANAGER] Excepción general en procesarUbicacion: ${e.message}")
            e.printStackTrace()
            _estadoConfig.value = EstadoCarga.Error(e.message ?: "Error desconocido")
        }
    }

    /**
     * 🚀 NUEVO: Fuerza la recarga de la configuración actual (Pull to Refresh)
     */
    suspend fun recargarConfiguracionActual() {
        if (lastLat != 0.0 || lastLng != 0.0) {
            procesarUbicacion(lastLat, lastLng, lastCP)
        }
    }

    private fun limpiarDatos() {
        configActiva = null
        zonaData = null
    }

    fun resetEstado() {
        _estadoConfig.value = EstadoCarga.Vacio
        limpiarDatos()
    }
}

/**
 * Estados de carga para manejar la UI
 */
sealed class EstadoCarga {
    object Vacio : EstadoCarga()
    object Cargando : EstadoCarga()
    object SinCobertura : EstadoCarga()
    data class Exito(
        val config: ConfiguracionZona,
        val id_zona: String,
        val nombreZona: String
    ) : EstadoCarga()
    data class Error(val mensaje: String) : EstadoCarga()
}